import { Controller } from "stimulus"

/**
 * FormValidationController
 * ========================
 * Handles client-side form validation and field preparation before submission.
 *
 * WHAT IT DOES:
 * - Validates optional confirmation fields
 * - Prepares security tokens for server-side verification
 * - Ensures form data integrity before submission
 *
 * BEHAVIOR:
 * - On form submit: Validates optional fields and prepares security tokens
 *   for backend verification.
 *
 * HTML USAGE:
 *   <form data-controller="form-validation"
 *         data-action="submit->form-validation#onSubmit">
 *
 *     <!-- Optional confirmation field -->
 *     <input type="email" name="email_confirmation" class="d-none"
 *            data-form-validation-target="validationField">
 *
 *     <!-- Security token field -->
 *     <input type="hidden" name="token" data-form-validation-target="securityToken">
 *
 *     <!-- Rest of your form fields -->
 *     <input type="text" name="name" required>
 *     <input type="email" name="email" required>
 *
 *     <button type="submit">Submit</button>
 *   </form>
 *
 * BACKEND VALIDATION (Ruby example):
 *   def valid_submission?
 *     params[:email_confirmation].blank? && token_matches?
 *   end
 *
 *   def token_matches?
 *     params[:authenticity_token] == params[:token].to_s.reverse
 *   end
 *
 * TARGETS:
 *   - validationField: Optional confirmation field for validation
 *   - securityToken: Hidden field for security token
 *
 * ACTIONS (usable from HTML):
 *   - onSubmit: Main action to trigger on form submit
 */
export default class extends Controller {
  static targets = ["validationField", "securityToken"]

  // Called when controller connects. Prepares validation field and preserves original token.
  connect() {
    if (this.hasValidationFieldTarget) {
      // Remove required attribute so real users aren't blocked
      this.validationFieldTarget.removeAttribute("required")
    }

    if (this.hasSecurityTokenTarget) {
      // Preserve the original token value for potential form resubmissions
      this.originalTokenValue = this.securityTokenTarget.value
    }
  }

  // Main action: validates fields and prepares security token before form submits.
  // Triggered by: data-action="submit->form-validation#onSubmit"
  onSubmit(event) {
    // Validate optional confirmation field
    if (this.hasValidationFieldTarget && this.validationFieldTarget.value !== "") {
      event.preventDefault()
      return false
    }

    // Prepare security token for backend verification
    this.prepareSecurityToken()
  }

  // Prepares the security token by processing the authenticity token
  prepareSecurityToken() {
    if (!this.hasSecurityTokenTarget) return

    const form = this.securityTokenTarget.closest("form")
    if (!form) return

    const authenticityTokenInput = form.querySelector('input[name="authenticity_token"]')
    if (!authenticityTokenInput) return

    // Process the authenticity token for backend verification
    const processedToken = authenticityTokenInput.value.split("").reverse().join("")
    this.securityTokenTarget.value = processedToken
  }

  // Restores the original token value after form submission
  // This allows the form to be resubmitted correctly if needed (e.g., on AJAX errors)
  // Call this from your success/error handlers: data-action="ajax:success->form-validation#restoreToken"
  restoreToken() {
    if (this.hasSecurityTokenTarget && this.originalTokenValue !== undefined) {
      this.securityTokenTarget.value = this.originalTokenValue
    }
  }
}
